/**
 * scaffoldDefaults.js
 *
 * Pré-preenchimento inteligente do formulário após seleção de entidade no Escopo.
 * Disparado apenas em criação (isEdit = false). Modelo v3: tipos catalogados = int.
 *
 * MESCLA com os blocos do preset — nunca substitui. O preset do tema traz a ESTRUTURA
 * (professional = 10 blocos: Hero, About, Services, Schedules, Experience, Gallery,
 * Testimonials, Faq, Contact, SocialLinks) e o scaffold preenche só os campos que
 * dependem da entidade escolhida no Escopo. Bloco de tipo ausente no preset é
 * acrescentado ao final; nenhum bloco do preset é removido.
 */
import { BlockType } from '../catalogs/pageCatalog.generated';

/**
 * Descarta chaves vazias para que o scaffold nunca sobrescreva um valor do preset
 * com '', null ou []. O preset é o piso; o scaffold só melhora o que tem dado real.
 */
function omitEmpty(obj) {
  const out = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (typeof value === 'string' && value.trim() === '') return;
    if (Array.isArray(value) && value.length === 0) return;
    out[key] = value;
  });
  return out;
}

/**
 * Aplica patches de config sobre os blocos do preset.
 *
 * @param {Array}  presetBlocks - blocos vindos de GET /pages/preset (já no form)
 * @param {Object} patches      - { [BlockType]: configParcial }
 * @returns {Array} blocos mesclados (preset preservado, patch por cima)
 */
function mergeIntoPresetBlocks(presetBlocks, patches) {
  const blocks = (Array.isArray(presetBlocks) ? presetBlocks : []).map((b) => ({ ...b }));

  Object.entries(patches).forEach(([typeKey, patch]) => {
    if (!patch || Object.keys(patch).length === 0) return;
    const type = Number(typeKey);
    const existing = blocks.find((b) => b.type === type);

    if (existing) {
      // Preset manda no que o patch não traz (ctaLabel, selectorKind, priorityOrder…)
      existing.config = { ...(existing.config || {}), ...patch };
    } else {
      blocks.push({
        id: crypto.randomUUID(), // UUID estável — nunca alterado (O8)
        type,
        enabled: true,
        order: blocks.length,
        config: patch,
      });
    }
  });

  return blocks.map((b, i) => ({ ...b, order: b.order ?? i }));
}

/**
 * Gera defaults para professional ou link_in_bio a partir do profissional selecionado.
 *
 * Retorna um mapa de caminhos RHF → valor. Usa caminhos pontuados (`seo.title`) em vez do
 * objeto inteiro para não zerar irmãos que o scaffold não conhece (`identity.avatarShape`,
 * `seo.ogImageUrl`).
 *
 * @param {Object} professional    - ProfessionalDto: name, title, specialty, bio, description, phone, email, photoUrl
 * @param {number} siteType        - PageType (mantido para simetria de assinatura com o call site)
 * @param {Array}  placesSchedules - ProfessionalPlaceScheduleDto[]: { schedules: [{ id, name, isActive }] }
 * @param {Array}  presetBlocks    - blocos atuais do form (preset do tema)
 * @returns {Object} { 'seo.title'?, 'seo.description'?, 'identity.avatarUrl'?, blocks }
 */
export function scaffoldProfessional(professional, siteType, placesSchedules = [], presetBlocks = []) {
  // A API serve `isActive` (ProfessionalScheduleInfoDto.IsActive). O campo `active` não existe
  // nesse contrato: o filtro antigo devolvia sempre [] e o bloco de agenda nunca era
  // preenchido — página nascia sem nenhuma agenda.
  const scheduleIds = placesSchedules
    .flatMap((ps) => ps.schedules || [])
    .filter((s) => s.isActive)
    .map((s) => s.id);

  const blocks = mergeIntoPresetBlocks(presetBlocks, {
    [BlockType.Hero]: omitEmpty({
      title: professional.name,
      subtitle: professional.specialty || professional.title,
    }),
    [BlockType.About]: omitEmpty({
      html: professional.bio || professional.description,
    }),
    [BlockType.Schedules]: omitEmpty({ scheduleIds }),
    [BlockType.Contact]: omitEmpty({
      phone: professional.phone,
      email: professional.email,
    }),
  });

  return {
    ...omitEmpty({
      'seo.title': professional.name,
      'seo.description': professional.description || professional.bio,
      'identity.avatarUrl': professional.photoUrl,
    }),
    blocks,
  };
}

/**
 * Gera defaults para company a partir do local selecionado.
 *
 * @param {Object} place        - PlaceDto: name, description, address, addressStreet, addressNumber,
 *                                addressNeighborhood, addressCity, addressState, phone, email
 * @param {Array}  presetBlocks - blocos atuais do form (preset do tema)
 * @returns {Object} { 'seo.title'?, 'seo.description'?, blocks }
 */
export function scaffoldCompany(place, presetBlocks = []) {
  const blocks = mergeIntoPresetBlocks(presetBlocks, {
    [BlockType.Hero]: omitEmpty({
      title: place.name,
      subtitle: place.description,
    }),
    // O bloco de Locais NÃO recebe mais `items`. Ele exibe todos os locais do cadastro, lidos ao
    // vivo do catálogo no render — e semear a cópia aqui a devolveria ao banco na criação, dando de
    // volta o defeito que a Fase 3 removeu: endereço que envelhece na primeira edição do cadastro.
    // Ver docs/ANALISE-REFERENCIAS-DINAMICAS-NAS-PAGINAS.md §7.
    //
    // O Contato abaixo continua copiando telefone/e-mail de propósito: são campos de CONTATO DA
    // PÁGINA, que o dono edita livremente ali e que podem ser diferentes do local — não são a mesma
    // informação com duas fontes.
    [BlockType.Contact]: omitEmpty({
      phone: place.phone,
      email: place.email,
    }),
  });

  return {
    ...omitEmpty({
      'seo.title': place.name,
      'seo.description': place.description,
    }),
    blocks,
  };
}
